import Decimal from 'decimal.js';
import * as yup from 'yup';

import { Category, DietaryPreference, MandatoryWarningCSVData } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { isDefined } from 'common/utils';

import getMaxCurrencyDecimals from '../../components/getCurrencyMaxDecimals';
import { isValidBarcodeDigit, isValidBarcodeLength } from '../../validation';

export type CsvValidationContext = {
  currency: Currency;
  overTheCounterProductHierarchies: string[];
  categories: Category[];
};

export const yupTestBarcode = {
  name: 'barcodeCheckDigit',
  message: { id: 'validation.enterValidBarcodeDigit' },
  test: (value: string | undefined) => !value || (isValidBarcodeLength(value) && isValidBarcodeDigit(value)),
};

export const yupTestUpToDecimalPlaces = (decimals: number) => ({
  name: 'upToDecimalPlaces',
  message:
    decimals === 1 ? { id: 'validation.atMostOneDecimal' } : { id: 'validation.atMostNDecimals', values: { decimals } },
  test: (value: number | undefined) => value === undefined || new Decimal(value).decimalPlaces() <= decimals,
});

export const yupTestMonetaryDecimals = {
  name: 'monetaryDecimals',
  test: (value: number | null | undefined, context: yup.TestContext<CsvValidationContext>) => {
    const decimals = context.options.context?.currency ? getMaxCurrencyDecimals(context.options.context.currency) : 2;
    if (value !== undefined && value !== null && new Decimal(value).decimalPlaces() > decimals) {
      return context.createError({
        message:
          decimals === 0
            ? { id: 'validation.mustBeInteger' }
            : { id: 'validation.atMostNDecimals', values: { decimals } },
      });
    }
    return true;
  },
};

export const yupTestProductHierarchyForIsOverTheCounter: yup.TestConfig<string | undefined, CsvValidationContext> = {
  name: 'productHierarchyForIsOverTheCounter',
  test: (value, context) => {
    const otcHierarchies = context?.options.context?.overTheCounterProductHierarchies ?? [];
    const isOverTheCounter: 'YES' | 'NO' | undefined = context.parent.is_over_the_counter;

    if (isOverTheCounter === 'YES' && (!value || !otcHierarchies.includes(value))) {
      return context.createError({
        message: { id: 'menu.csvItemUpdate.invalidProductHierarchyForOverTheCounterItem' },
      });
    }
    return true;
  },
};

export const yupTestEitherInventoryMode = {
  name: 'eitherInventoryMode',
  message: { id: 'validation.eitherInventoryMode' },
  test: (value: 'YES' | 'NO' | undefined, context: yup.TestContext<Record<string, any>>) =>
    value !== 'YES' || context.parent.use_limited_quantity !== 'YES',
};

export const yupTestMissingWeightStep = {
  name: 'yupTestMissingWeightStep',
  test: (value: 'weight' | 'piece' | undefined, context: yup.TestContext<Record<string, any>>) => {
    if (
      (value === 'weight' && !context.parent.weighted_item_min_weight_in_grams) ||
      (value === 'piece' && !context.parent.weighted_item_approx_weight_in_grams)
    ) {
      return context.createError({ message: { id: 'validation.missingWeightStep' } });
    }
    return true;
  },
};

export const yupNoUnallowedCombination = {
  name: 'yupNoUnallowedCombination',
  test: (value: 'weight' | 'piece' | undefined, context: yup.TestContext<Record<string, any>>) => {
    const fields = [
      context.parent.weight_in_grams !== undefined ? 'weight_in_grams' : undefined,
      context.parent.volume_in_ml !== undefined ? 'volume_in_ml' : undefined,
      context.parent.number_of_units !== undefined ? 'number_of_units' : undefined,
      context.parent.use_inventory_management === 'YES' ? 'use_inventory_management' : undefined,
      context.parent.use_limited_quantity === 'YES' ? 'use_limited_quantity' : undefined,
      context.parent.is_in_stock !== undefined ? 'is_in_stock' : undefined,
    ]
      .filter(isDefined)
      .sort();
    if (value !== undefined && fields.length > 0) {
      return context.createError({
        message: {
          id: 'menu.csvItemUpdate.invalidWeightBasedFields',
          values: { fields: fields.join(', ') },
        },
      });
    }
    return true;
  },
};

export const yupBinaryInventory = {
  name: 'yupBinaryInventory',
  test: (value: 'YES' | 'NO' | undefined, context: yup.TestContext<Record<string, any>>) => {
    if (value !== undefined && context.parent.use_limited_quantity === 'YES') {
      return context.createError({
        message: {
          id: 'menu.csvItemUpdate.cannotUseBinaryInventory',
          values: { nonBinaryMode: 'use_limited_quantity' },
        },
      });
    }
    if (value !== undefined && context.parent.use_inventory_management === 'YES') {
      return context.createError({
        message: {
          id: 'menu.csvItemUpdate.cannotUseBinaryInventory',
          values: { nonBinaryMode: 'use_inventory_management' },
        },
      });
    }
    return true;
  },
};

export const yupTestCategoryMismatch: yup.TestConfig<string | undefined, CsvValidationContext> = {
  name: 'yupTestCategoryMismatch',
  message: { id: 'menu.csvItemUpdate.categoryMismatch' },
  test: (value, context) => {
    const contextCategories = context.options.context?.categories;
    const categoryId = context.parent.category_id;
    if (!value || !categoryId || !contextCategories || contextCategories.length === 0) {
      return true;
    }
    return !!contextCategories.find(category => category.id === categoryId)?.name.some(name => name.value === value);
  },
};

export const yupTestMultipleCategoryMatches: yup.TestConfig<string | undefined, CsvValidationContext> = {
  name: 'yupTestMultipleCategoryMatches',
  message: { id: 'menu.csvItemUpdate.multipleCategoryMatches' },
  test: (value, context) => {
    const contextCategories = context.options.context?.categories;
    const matchedCategories = contextCategories?.filter(category => category.name.some(name => name.value === value));
    if (!value || context.parent.category_id || !matchedCategories || matchedCategories.length === 0) {
      return true;
    }
    return matchedCategories.length === 1;
  },
};

const csvMandatoryWarnings: MandatoryWarningCSVData[] = [
  'alcohol_hard',
  'alcohol_soft',
  'choking_nuts',
  'choking_sausages',
  'fat',
  'otc',
  'sodium',
  'sugar',
];

export const yupTestMandatoryWarnings: yup.TestConfig<string | undefined, CsvValidationContext> = {
  name: 'yupTestMandatoryWarnings',
  test: (value, context) => {
    if (
      value === undefined ||
      value.split(' ').every(v => csvMandatoryWarnings.includes(v as MandatoryWarningCSVData))
    ) {
      return true;
    }

    return context.createError({
      message: {
        id: 'menu.csvItemUpdate.invalidFieldValues',
        values: {
          fieldValues: csvMandatoryWarnings.join(', '),
        },
      },
    });
  },
};

export const yupTestDeposits: yup.TestConfig<number | undefined, CsvValidationContext> = {
  name: 'yupTestDeposits',
  test: (value, context) => {
    if (value === undefined || context.parent.weighted_item_sell_by !== 'weight') {
      return true;
    }

    return context.createError({
      message: {
        id: 'menu.csvItemUpdate.invalidDeposits',
      },
    });
  },
};

const dietaryPreferences: DietaryPreference[] = [
  'gluten-free',
  'halal',
  'kosher',
  'lactose-free',
  'vegan',
  'vegetarian',
];

export const yupTestDietaryPreferences: yup.TestConfig<string | undefined, CsvValidationContext> = {
  name: 'yupTestDietaryPreferences',
  test: (value, context) => {
    if (value === undefined || value.split(' ').every(v => dietaryPreferences.includes(v as DietaryPreference))) {
      return true;
    }

    return context.createError({
      message: {
        id: 'menu.csvItemUpdate.invalidFieldValues',
        values: {
          fieldValues: dietaryPreferences.join(', '),
        },
      },
    });
  },
};
